// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  chtDistributionValues,
  chtHeatBandSummary,
  egtDistributionSummary,
  egtDistributionValues,
  egtSpreadTimelineSummary,
  oilSystemTrendSummary,
} from "../trends/trend-row-builder.js";
import {
  analyzeChtCoolingRate,
  coolingRateTraceForKey,
} from "../analysis/cooling.js";
import {
  egtRhythmWaveforms,
} from "../analysis/valve.js";
import {
  analyzeCombustionSignatures,
  chtEgtPairingEvaluationState,
  chtEgtPairingRoster,
  chtEgtPairingTraceForKey,
} from "../analysis/combustion.js";
import {
  analyzeElectrical,
  electricalBusTrace,
} from "../analysis/electrical.js";
import { projectFlightDataForEngine } from "../core/engines.js";

function finite(value) {
  // Parsers emit explicit null for blank cells, and Number(null) is 0 — reject
  // nullish/empty before coercing or missing data turns into real zeros.
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function quantileSorted(sorted, fraction) {
  if (!sorted.length) return null;
  const position = Math.max(0, Math.min(sorted.length - 1, (sorted.length - 1) * fraction));
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

function quantile(values, fraction) {
  const sorted = (values ?? []).map(finite).filter(Number.isFinite).sort((a, b) => a - b);
  return quantileSorted(sorted, fraction);
}

function downsampleSeries(series, limit = 120) {
  const points = (Array.isArray(series) ? series : [])
    .map((point, index) => ({
      t: finite(point.t ?? point.elapsedSeconds ?? index),
      value: finite(point.value ?? point.cht ?? point.rate),
    }))
    .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.value));
  if (points.length <= limit) return points;
  const step = Math.max(1, Math.ceil(points.length / limit));
  const sampled = points.filter((_, index) => index % step === 0);
  if (sampled.at(-1) !== points.at(-1)) sampled.push(points.at(-1));
  return sampled;
}

// Per-cylinder statistics straight from the source samples. Quantiles are
// exact over every gated sample (no per-bin cap that would reweight the
// distribution) and `max` is the true maximum, never a percentile standing
// in for a peak (issue #480).
function cylinderValueStats(items) {
  const byCylinder = new Map();
  (items ?? []).forEach((item) => {
    const sorted = (item.values ?? []).map(finite).filter(Number.isFinite).sort((a, b) => a - b);
    if (!sorted.length) return;
    byCylinder.set(item.cylinder, {
      p50: quantileSorted(sorted, 0.5),
      p90: quantileSorted(sorted, 0.9),
      p95: quantileSorted(sorted, 0.95),
      max: sorted[sorted.length - 1],
      samples: sorted.length,
    });
  });
  return byCylinder;
}

function durationLabel(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0s";
  const rounded = Math.round(seconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

function firstFiniteForKeys(point, keys) {
  for (const key of keys) {
    const value = finite(point?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function downsampleItems(items, limit = 180) {
  if (items.length <= limit) return items;
  const step = Math.max(1, Math.ceil(items.length / limit));
  const sampled = items.filter((_, index) => index % step === 0);
  if (sampled.at(-1) !== items.at(-1)) sampled.push(items.at(-1));
  return sampled;
}

function oilTimelineSamples(points, limit = 220) {
  const inBounds = (value, min, max) => (Number.isFinite(value) && value >= min && value <= max ? value : null);
  const samples = (Array.isArray(points) ? points : [])
    .map((point) => ({
      // Rows without a real timestamp are dropped rather than charted at the
      // array index, which would zigzag the time axis.
      t: finite(point?.elapsedSeconds),
      // Bounds apply per metric: a glitched value on one channel must not ride
      // into the plot just because the other channel qualified the sample.
      pressure: inBounds(firstFiniteForKeys(point, ["oilPressure", "oilPressure1", "oilPressure2"]), 0, 140),
      temperature: inBounds(firstFiniteForKeys(point, ["oilTemp", "oilTemp1", "oilTemp2"]), 60, 260),
      rpm: firstFiniteForKeys(point, ["rpm", "rpm1", "rpm2"]),
    }))
    .filter((sample) => (
      Number.isFinite(sample.t)
      && (!Number.isFinite(sample.rpm) || sample.rpm >= 700)
      && (sample.pressure !== null || sample.temperature !== null)
    ))
    .map((sample) => ({
      t: sample.t,
      pressure: sample.pressure === null ? null : Math.round(sample.pressure * 10) / 10,
      temperature: sample.temperature === null ? null : Math.round(sample.temperature * 10) / 10,
    }));
  return downsampleItems(samples, limit);
}

function heatVisual(points, engineDescriptors) {
  const heatRows = chtHeatBandSummary(points, { engineDescriptors });
  if (!heatRows.length) return null;
  const distStats = cylinderValueStats(chtDistributionValues(points, { engineDescriptors }));
  const rows = heatRows.map((row) => {
    const operatingMinutes = finite(row.operatingMinutes ?? (row.operatingSeconds / 60)) ?? 0;
    const shownMinutes = (row.bands ?? []).reduce((sum, band) => sum + (finite(band.minutes) ?? 0), 0);
    const belowMinutes = Math.max(0, operatingMinutes - shownMinutes);
    const bands = [
      { key: "below-325", label: "<325 F", minutes: belowMinutes },
      ...(row.bands ?? []).map((band) => ({
        key: band.key,
        label: band.label,
        minutes: finite(band.minutes) ?? 0,
      })),
    ];
    const hotMinutes = bands
      .filter((band) => band.key === "400-424" || band.key === "425+")
      .reduce((sum, band) => sum + band.minutes, 0);
    const warmMinutes = bands
      .filter((band) => band.key === "375-399" || band.key === "400-424" || band.key === "425+")
      .reduce((sum, band) => sum + band.minutes, 0);
    return {
      cylinder: row.cylinder,
      label: row.shortLabel ?? row.label ?? `Cyl ${row.cylinder}`,
      operatingMinutes,
      warmMinutes,
      hotMinutes,
      p95: distStats.get(row.cylinder)?.p95 ?? null,
      max: distStats.get(row.cylinder)?.max ?? null,
      bands,
    };
  }).filter((row) => row.operatingMinutes > 0);
  if (!rows.length) return null;
  const hottest = rows.slice().sort((a, b) => b.hotMinutes - a.hotMinutes || (b.p95 ?? 0) - (a.p95 ?? 0))[0] ?? null;
  const peak = rows.filter((row) => Number.isFinite(row.max)).sort((a, b) => b.max - a.max)[0] ?? null;
  return {
    key: "chtHeat",
    type: "heat-bars",
    title: "CHT Heat Exposure",
    subtitle: "Cumulative operating time in each CHT band by cylinder.",
    available: true,
    model: {
      rows,
      stats: [
        { label: "Cylinders", value: String(rows.length), detail: "with warm CHT data" },
        { label: "Most 400 F+", value: hottest?.label ?? "--", detail: hottest ? `${Math.round(hottest.hotMinutes)} min` : "" },
        { label: "Peak CHT", value: Number.isFinite(peak?.max) ? `${Math.round(peak.max)} F` : "--", detail: peak?.label ?? "" },
      ],
    },
  };
}

// On twins the first-finite-key fallback would blend both engines into one
// trace that reads like a single engine's continuous trend while silently
// switching sources whenever one sensor drops out — so render one trend per
// engine, over points projected to that engine's channels.
function oilVisuals(points, summary, engineDescriptors) {
  const multiEngine = (engineDescriptors ?? []).filter((engine) => engine?.side && engine.side !== "single");
  if (multiEngine.length < 2) return [oilVisual(points, summary)];
  return multiEngine.map((engine) => {
    const visual = oilVisual(projectFlightDataForEngine(points, engine), summary);
    if (!visual) return null;
    return {
      ...visual,
      key: `oilSystem-${engine.id ?? engine.side}`,
      title: `Oil System Trend — ${engine.label ?? engine.side}`,
    };
  }).filter(Boolean);
}

function oilVisual(points, summary) {
  const oilTrend = oilSystemTrendSummary(points, quantile);
  const samples = oilTimelineSamples(points);
  if (!samples.length) return null;
  return {
    key: "oilSystem",
    type: "oil-trend",
    title: "Oil System Trend",
    subtitle: "Oil pressure and oil temperature through the flight, with the warm pressure range called out.",
    available: true,
    model: {
      samples: samples.slice(0, 450),
      pressureMedian: oilTrend.pressureMedian,
      pressureP10: oilTrend.pressureP10,
      pressureP90: oilTrend.pressureP90,
      pressureMin: oilTrend.pressureMin,
      pressureMax: oilTrend.pressureMax,
      temperatureMedian: oilTrend.temperatureMedian,
      temperatureP10: oilTrend.temperatureP10,
      temperatureP90: oilTrend.temperatureP90,
      temperatureMin: oilTrend.temperatureMin,
      temperatureMax: oilTrend.temperatureMax,
      sampleCount: oilTrend.sampleCount ?? samples.length,
      note: summary?.oilSystem?.note ?? "",
      score: summary?.oilSystem?.score,
    },
  };
}

function egtRhythmVisual(points, summary) {
  const thresholdF = 30;
  const details = summary?.valve?.details;
  if (!details?.top?.best) return null;
  const model = egtRhythmWaveforms(points, details);
  if (!model?.available) return null;
  const selected = model.cylinders.find((lane) => lane.cylinder === model.worstCylinder) ?? model.cylinders[0] ?? null;
  const displaySeries = selected?.baselineSeries?.length ? selected.baselineSeries : selected?.residualSeries ?? [];
  if (!displaySeries.length) return null;
  if ((finite(selected.amplitude) ?? 0) < thresholdF) return null;
  return {
    key: "egtRhythm",
    type: "waveform",
    title: "EGT Rhythm Signature",
    subtitle: "Per-cylinder EGT movement in the selected steady-power window.",
    available: true,
    model: {
      label: selected.label,
      floorF: model.floorF,
      warningF: model.warningF,
      window: model.window,
      residualSeries: downsampleSeries(displaySeries, 160),
      fitSeries: downsampleSeries(selected.fitSeries, 160),
      stats: [
        { label: "Cylinder", value: selected.label, detail: "largest wave" },
        { label: "Wave size", value: Number.isFinite(selected.amplitude) ? `${Math.round(selected.amplitude)} F` : "--", detail: "peak-to-peak" },
        { label: "Rhythm speed", value: Number.isFinite(selected.frequencyCpm) ? `${selected.frequencyCpm.toFixed(1)} cpm` : "--", detail: "cycles/min" },
        { label: "Peer contrast", value: Number.isFinite(selected.ratio) ? `${selected.ratio.toFixed(1)}x` : "--", detail: "vs peers" },
      ],
    },
  };
}

function coolingRateVisual(points, summary) {
  const persisted = summary?.coolingRate?.details?.summaries;
  const analysis = Array.isArray(persisted) && persisted.length
    ? { details: { summaries: persisted, top: summary?.coolingRate?.details?.top ?? null, thresholdFpm: summary?.coolingRate?.details?.thresholdFpm ?? 50 } }
    : analyzeChtCoolingRate(points);
  const summaries = (analysis.details?.summaries ?? []).filter((item) => Number.isFinite(item?.cylinder));
  if (!summaries.length) return null;
  const ranked = summaries.slice().sort((a, b) => (finite(b.maxRateFpm) ?? -Infinity) - (finite(a.maxRateFpm) ?? -Infinity));
  const topSummary = ranked.find((item) => item.top === analysis.details?.top)
    ?? ranked.find((item) => item.key === analysis.details?.top?.key)
    ?? ranked[0];
  const trace = topSummary?.key ? coolingRateTraceForKey(points, topSummary.key) : null;
  const threshold = finite(analysis.details?.thresholdFpm) ?? 50;
  const topRate = finite(topSummary?.maxRateFpm)
    ?? finite(analysis.details?.top?.rateFpm)
    ?? finite(analysis.details?.top?.maxRateFpm);
  if (!Number.isFinite(topRate) || topRate < threshold) return null;
  return {
    key: "coolingRate",
    type: "cooling-rate",
    title: "Cooling Rate",
    subtitle: "Fastest CHT cooling per cylinder, with the steepest trace when available.",
    available: true,
    model: {
      tiles: ranked.map((item) => ({
        label: item.label ?? `Cyl ${item.cylinder}`,
        value: Number.isFinite(item.maxRateFpm) ? `${Math.round(item.maxRateFpm)} F/min` : "--",
        detail: (item.maxRateFpm ?? 0) >= threshold ? "review band" : "below review",
        band: (item.maxRateFpm ?? 0) >= threshold ? "review" : (item.maxRateFpm ?? 0) >= 40 ? "warm" : "comfortable",
      })),
      trace: trace?.samples?.length >= 4 ? {
        label: trace.label ?? topSummary.label ?? `Cyl ${topSummary.cylinder}`,
        samples: downsampleSeries(trace.samples.map((sample) => ({ t: sample.t, value: sample.rate })), 160),
        event: topSummary.top ?? null,
        yLabel: "F/min",
        thresholds: [
          { value: 40, label: "40 compare", band: "warm" },
          { value: threshold, label: `${Math.round(threshold)} review`, band: "review" },
        ],
      } : null,
    },
  };
}

function egtBalanceVisual(points, summary, engineDescriptors) {
  const lanes = egtDistributionSummary(points, { engineDescriptors });
  const spread = egtSpreadTimelineSummary(points, { engineDescriptors });
  if (!lanes.length && !spread.samples?.length) return null;
  const stats = cylinderValueStats(egtDistributionValues(points, { engineDescriptors }));
  const tiles = lanes.map((lane) => {
    const item = stats.get(lane.cylinder);
    return {
      label: lane.shortLabel ?? lane.label ?? `Cyl ${lane.cylinder}`,
      value: Number.isFinite(item?.p90) ? `${Math.round(item.p90)} F` : "--",
      detail: `${item?.samples ?? lane.samples ?? 0} loaded samples`,
      band: "comfortable",
    };
  });
  return {
    key: "egtBalance",
    type: "egt-balance",
    title: "EGT Balance",
    subtitle: "Per-cylinder loaded EGT lanes and hottest-minus-coolest spread through the flight.",
    available: true,
    model: {
      tiles,
      trace: spread.samples?.length >= 4 ? {
        label: "EGT spread",
        samples: downsampleSeries(spread.samples.map((sample) => ({ t: sample.t, value: sample.spread })), 160),
        yLabel: "F",
        thresholds: [
          { value: 60, label: "60 balanced", band: "warm" },
          { value: 90, label: "90 review", band: "review" },
        ],
      } : null,
      stats: [
        { label: "Loaded P90", value: Number.isFinite(spread.loadedSpreadP90) ? `${Math.round(spread.loadedSpreadP90)} F` : "--", detail: "spread" },
        { label: "Max spread", value: Number.isFinite(spread.maxSpread) ? `${Math.round(spread.maxSpread)} F` : "--", detail: "all samples" },
        { label: "Cylinders", value: String(spread.cylinderCount || lanes.length), detail: "EGT channels" },
      ],
      score: summary?.egt?.score,
    },
  };
}

function sensorTrustVisual(summary) {
  const summaries = summary?.sensors?.details?.summaries;
  if (!Array.isArray(summaries) || !summaries.length) return null;
  const markedWindowCount = (sensor) => (sensor.conditionWindows ?? []).length + (sensor.flatline ? 1 : 0) + (sensor.jumpEvents ?? []).length;
  const ranked = summaries.slice().sort((a, b) => {
    const markDelta = markedWindowCount(b) - markedWindowCount(a);
    if (markDelta) return markDelta;
    return (finite(b.suspicion) ?? 0) - (finite(a.suspicion) ?? 0);
  });
  const problemCount = summaries.filter((sensor) => markedWindowCount(sensor) > 0 || (finite(sensor.suspicion) ?? 0) > 0).length;
  return {
    key: "sensorTrust",
    type: "sensor-trust",
    title: "Sensor Trust",
    subtitle: "Probe/channel reliability signals that affect confidence in the engine read.",
    available: true,
    model: {
      tiles: ranked.slice(0, 8).map((sensor) => {
        const marks = markedWindowCount(sensor);
        const coverage = Math.max(0, Math.min(1, 1 - (finite(sensor.missingRatio) ?? 0)));
        const suspicion = finite(sensor.suspicion) ?? 0;
        return {
          label: sensor.label ?? sensor.key ?? "Channel",
          value: marks ? `${marks} mark${marks === 1 ? "" : "s"}` : `${Math.round(coverage * 100)}%`,
          detail: marks
            ? (sensor.conditionWindows?.[0]?.reason ?? sensor.flatline?.reason ?? "signal anomaly")
            : "trusted coverage",
          band: marks || suspicion >= 30 ? "review" : suspicion > 0 ? "warm" : "comfortable",
        };
      }),
      stats: [
        { label: "Channels", value: String(summaries.length), detail: "checked" },
        { label: "Marked", value: String(problemCount), detail: "with trust cues" },
        { label: "Score", value: Number.isFinite(summary?.sensors?.score) ? `${Math.round(summary.sensors.score)}/100` : "--", detail: summary?.sensors?.note ?? "" },
      ],
    },
  };
}

function pairingVisual(points, summary, engineDescriptors) {
  const pairingMetric = summary?.combustion ?? analyzeCombustionSignatures(points, { engineDescriptors });
  const persisted = pairingMetric?.details;
  const candidates = Array.isArray(persisted?.candidates) ? persisted.candidates : [];
  const evaluation = chtEgtPairingEvaluationState(pairingMetric);
  const roster = chtEgtPairingRoster(points, { engineDescriptors });
  if (!roster.length) return null;
  const candidateByCylinder = new Map();
  candidates.forEach((candidate) => {
    if (!Number.isFinite(candidate?.cylinder)) return;
    const existing = candidateByCylinder.get(candidate.cylinder);
    if (!existing || (candidate.suspicion ?? 0) > (existing.suspicion ?? 0)) candidateByCylinder.set(candidate.cylinder, candidate);
  });
  const topCandidate = [...candidateByCylinder.values()].sort((a, b) => (b.suspicion ?? 0) - (a.suspicion ?? 0))[0] ?? null;
  const focusCylinder = topCandidate?.cylinder ?? roster[0]?.cylinder;
  const trace = Number.isFinite(focusCylinder) ? chtEgtPairingTraceForKey(points, `cht${focusCylinder}`, { engineDescriptors }) : null;
  return {
    key: "chtEgtPairing",
    type: "pairing",
    title: "CHT/EGT Pairing",
    subtitle: "Whether each cylinder's CHT tracks its EGT, splits away, or lacked an eligible settled window.",
    available: true,
    model: {
      tiles: roster.map((item) => {
        const candidate = candidateByCylinder.get(item.cylinder);
        const evaluation = chtEgtPairingEvaluationState(pairingMetric, item.engineKey);
        return {
          label: item.engineShortLabel ? `${item.engineShortLabel} Cyl ${item.displayNumber ?? item.cylinder}` : `Cyl ${item.displayNumber ?? item.cylinder}`,
          value: candidate ? "Split" : evaluation.evaluated ? "Paired" : "Not evaluated",
          detail: candidate
            ? (candidate.label ?? `EGT ${Math.round(candidate.egtChange ?? 0)} F / CHT ${Math.round(candidate.chtChange ?? 0)} F`)
            : evaluation.evaluated
              ? "CHT tracks EGT"
              : evaluation.reason,
          band: candidate
            ? ((candidate.suspicion ?? 0) >= 40 ? "review" : "warm")
            : evaluation.evaluated
              ? "comfortable"
              : "neutral",
        };
      }),
      trace: trace?.samples?.length >= 4 && trace.hasCht && trace.hasEgt ? {
        label: trace.label,
        samples: downsampleSeries(trace.samples.map((sample) => ({ t: sample.t, value: sample.cht })), 160),
        secondarySamples: downsampleSeries(trace.samples.map((sample) => ({ t: sample.t, value: sample.egt })), 160),
        peerSamples: downsampleSeries(trace.samples.map((sample) => ({ t: sample.t, value: sample.peerCht })), 160),
        secondaryPeerSamples: downsampleSeries(trace.samples.map((sample) => ({ t: sample.t, value: sample.peerEgt })), 160),
        event: topCandidate && Number.isFinite(topCandidate.startTime) && Number.isFinite(topCandidate.endTime)
          ? { startTime: topCandidate.startTime, endTime: topCandidate.endTime }
          : null,
        yLabel: "CHT F",
        secondaryLabel: "EGT F",
      } : null,
      stats: [
        { label: "CHT+EGT channels", value: String(roster.length), detail: "available pairs" },
        { label: "Split cues", value: String(candidateByCylinder.size), detail: "cylinders" },
        { label: "Score", value: Number.isFinite(pairingMetric?.score) ? `${Math.round(pairingMetric.score)}/100` : "--", detail: pairingMetric?.note ?? "" },
      ],
      evaluation,
    },
  };
}

function electricalVisual(points, summary) {
  const persisted = summary?.electrical?.details;
  const details = Number.isFinite(persisted?.lowLimit) && Number.isFinite(persisted?.highLimit)
    ? persisted
    : analyzeElectrical(points).details;
  if (!Number.isFinite(details?.lowLimit) || !Number.isFinite(details?.highLimit)) return null;
  const trace = electricalBusTrace(points);
  const charging = details.chargingRecovery;
  const hasStartTime = Number.isFinite(charging?.startTime);
  const startZoom = hasStartTime
    ? electricalBusTrace(points, {
      startTime: charging.startTime - 30,
      endTime: Number.isFinite(charging.endTime) ? Math.max(charging.endTime, charging.startTime + 60) : charging.startTime + 90,
      maxBuckets: 240,
    })
    : null;
  return {
    key: "electrical",
    type: "electrical",
    title: "Electrical Bus",
    subtitle: "Bus voltage against the charging envelope, plus start-charge recovery when a start is captured.",
    available: true,
    model: {
      tiles: [
        {
          label: "Running bus",
          value: Number.isFinite(details.runningMedian) ? `${details.runningMedian.toFixed(1)} V` : "--",
          detail: Number.isFinite(details.runningMin) && Number.isFinite(details.runningMax)
            ? `${details.runningMin.toFixed(1)}-${details.runningMax.toFixed(1)} V`
            : "running voltage",
          band: details.runningStable ? "comfortable" : "warm",
        },
        {
          label: "Envelope",
          value: `${details.lowLimit.toFixed(1)}-${details.highLimit.toFixed(1)} V`,
          detail: details.isTwentyEightVolt ? "28V system" : "12V system",
          band: "comfortable",
        },
        {
          label: "Out of range",
          value: Number.isFinite(details.outRatio) ? `${Math.round(details.outRatio * 100)}%` : "--",
          detail: "engine-run time",
          band: (details.outRatio ?? 0) > 0.25 ? "review" : (details.outRatio ?? 0) > 0 ? "warm" : "comfortable",
        },
        {
          label: "Start charge",
          value: charging?.status ? String(charging.status).replace(/^./, (char) => char.toUpperCase()) : "--",
          detail: charging?.reason ?? "start not detected",
          band: charging?.status === "fail" ? "review" : charging?.status === "warning" ? "warm" : "comfortable",
        },
      ],
      trace: trace.hasVolts ? {
        label: "Bus voltage",
        samples: downsampleSeries(trace.volts, 180),
        yLabel: "V",
        thresholds: [
          { value: details.lowLimit, label: `${details.lowLimit.toFixed(1)} low`, band: "warm" },
          { value: details.highLimit, label: `${details.highLimit.toFixed(1)} high`, band: "warm" },
        ],
        event: details.outOfRangeWindow ?? null,
      } : null,
      startTrace: startZoom?.hasVolts ? {
        label: "Start recovery",
        samples: downsampleSeries(startZoom.volts, 140),
        yLabel: "V",
        thresholds: charging?.chargingLimit ? [
          { value: charging.chargingLimit, label: `${charging.chargingLimit.toFixed(1)} charge`, band: "comfortable" },
        ] : [],
        event: hasStartTime ? { startTime: charging.startTime, endTime: charging.startTime + 8 } : null,
      } : null,
      stats: [
        { label: "Score", value: Number.isFinite(summary?.electrical?.score) ? `${Math.round(summary.electrical.score)}/100` : "--", detail: summary?.electrical?.note ?? "" },
        { label: "Amps", value: trace.hasAmps ? "Logged" : "No", detail: "current channel" },
        { label: "Duration", value: durationLabel((trace.tMax ?? 0) - (trace.tMin ?? 0)), detail: "bus trace" },
      ],
    },
  };
}

export function buildReportOverviewVisuals({
  flightData,
  summary,
  selectedFlight,
  engineDescriptors,
} = {}) {
  const points = Array.isArray(flightData) ? flightData : [];
  if (!points.length) return [];
  const descriptors = Array.isArray(engineDescriptors) ? engineDescriptors : [];
  const visuals = [
    heatVisual(points, descriptors),
    ...oilVisuals(points, summary, descriptors),
    egtRhythmVisual(points, summary),
    coolingRateVisual(points, summary),
    egtBalanceVisual(points, summary, descriptors),
    sensorTrustVisual(summary),
    pairingVisual(points, summary, descriptors),
    electricalVisual(points, summary),
  ].filter((visual) => visual?.available);
  return visuals.map((visual) => ({
    ...visual,
    flightTitle: selectedFlight?.title ?? selectedFlight?.aircraft ?? "",
  }));
}
